import React from 'react'
import { Collapsible } from './ui/Collapsible'
import { SliderControl } from './controls/SliderControl'
import type { FieldDef, FieldValues } from '../gallery/fieldTypes'
import { getFieldGroups, getFieldsByGroup } from '../gallery/fieldTypes'
import { COLOR_OPTIONS } from '../utils/twColor'

interface DynamicControlPanelProps {
  fields: FieldDef[]
  values: FieldValues
  onChange: (id: string, value: any) => void
  onReplay: () => void
  onReset: () => void
}

function ColorControl({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COLOR_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          title={opt.label}
          className={`w-6 h-6 rounded-md cursor-pointer transition-all ${
            value === opt.value
              ? 'ring-2 ring-gray-900 ring-offset-2 scale-110'
              : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
          }`}
          style={{ backgroundColor: opt.hex }}
        />
      ))}
    </div>
  )
}

function FieldControl({ field, value, onChange }: { field: FieldDef; value: any; onChange: (v: any) => void }) {
  switch (field.type) {
    case 'slider':
      return (
        <SliderControl
          label={field.label}
          value={value}
          min={field.min}
          max={field.max}
          step={field.step}
          unit={field.unit}
          onChange={onChange}
        />
      )
    case 'select':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{field.label}</label>
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )
    case 'toggle':
      return (
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{field.label}</label>
          <button
            type="button"
            onClick={() => onChange(!value)}
            className={`w-9 h-5 rounded-full p-0.5 flex cursor-pointer transition-colors duration-200 ${
              value ? 'bg-indigo-500 justify-end' : 'bg-gray-300 justify-start'
            }`}
          >
            <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
          </button>
        </div>
      )
    case 'color':
      return (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{field.label}</label>
          <ColorControl value={value} onChange={onChange} />
        </div>
      )
    case 'text':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{field.label}</label>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      )
    default:
      return null
  }
}

export function DynamicControlPanel({ fields, values, onChange, onReplay, onReset }: DynamicControlPanelProps) {
  const groups = getFieldGroups(fields)

  return (
    <aside className="w-80 border-r border-gray-200 bg-white flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={onReplay}
            className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Replay
          </button>
          <button
            onClick={onReset}
            className="h-9 px-3 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {groups.map((group, i) => (
          <Collapsible key={group} title={group} defaultOpen={i < 3}>
            <div className="space-y-3">
              {getFieldsByGroup(fields, group).map((field) => (
                <FieldControl
                  key={field.id}
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => onChange(field.id, v)}
                />
              ))}
            </div>
          </Collapsible>
        ))}
      </div>
    </aside>
  )
}
